import React, { useState, useContext, useEffect, useRef } from "react";
import styled from "styled-components";
import { DateRangePicker } from "rsuite";
import { OrganizationContext } from "../contexts/OrganizationContext";
import { ProfileContext } from "../contexts/ProfileContext";
import {
  organizationTiers,
  organizationTypes,
  organizationDenominations,
  paymentTerms,
  countries,
  provinces,
    OrgActiveStatusList
} from "../assets/data";
import { CheckboxSelect } from "./checkbox-select";
import { predefinedBottomRanges } from "../assets/data";
import { MdClear, MdSearch } from "react-icons/md";
import { format } from "date-fns";

export const CALL_LOG_API_PARAMS = [
  "start_date",
  "end_date",
  "agent_name",
  "caller_number",
  "called_number",
  "date",
];

export const buildCallLogsFilterString = (searchParams) => {
  const params = new URLSearchParams();
  Object.entries(searchParams).forEach(([key, value]) => {
    if (key === "date_range" && Array.isArray(value) && value.length === 2) {
      params.set("start_date", format(value[0], "yyyy-MM-dd"));
      params.set("end_date", format(value[1], "yyyy-MM-dd"));
    } else if (key !== "date_range") {
      params.set(key, value);
    }
  });
  return params.toString();
};

export const parseCallLogsFilterString = (filterString) => {
  if (!filterString) return {};
  const parsed = new URLSearchParams(filterString);
  const result = {};
  parsed.forEach((value, key) => {
    if (key !== "start_date" && key !== "end_date") {
      result[key] = value;
    }
  });
  const startDate = parsed.get("start_date");
  const endDate = parsed.get("end_date");
  if (startDate && endDate) {
    result.date_range = [new Date(startDate), new Date(endDate)];
  }
  return result;
};

export const getCallLogsFilterStringFromUrl = (urlSearchParams, filterParam = "filter") => {
  const params = new URLSearchParams();
  CALL_LOG_API_PARAMS.forEach((key) => {
    const value = urlSearchParams.get(key);
    if (value) params.set(key, value);
  });
  if (params.toString()) return params.toString();
  return urlSearchParams.get(filterParam) || "";
};

//This component handles filter functionality for the different roles in anyone pray dashboard
export const Filter = ({
  setFilterString,
  page,
  defaultSearchBy,
  initialFilterString,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [searchBy, setSearchBy] = useState(defaultSearchBy || "");
  const [selectedRange, setSelectedRange] = useState([]);
  const [searchParams, setSearchParams] = useState(() =>
    page === "callLogs" && initialFilterString
      ? parseCallLogsFilterString(initialFilterString)
      : {}
  );
  const {
    filterByArray,
    setFilterByArray,
    filterValueArray,
    setFilterValueArray,
  } = useContext(OrganizationContext);
  const { setDateRange, dateRange, showReports } = useContext(ProfileContext);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [checkboxValues, setCheckboxValues] = useState([]);

  // Add a debounced search function for name searches
  const [nameSearchTimeout, setNameSearchTimeout] = useState(null);

  // Track if search button should be active
  const [isSearchActive, setIsSearchActive] = useState(false);

  // Update searchBy when defaultSearchBy prop changes
  useEffect(() => {
    if (defaultSearchBy) {
      setSearchBy(defaultSearchBy);
    }
  }, [defaultSearchBy]);

  // Helper function to check if a value is valid for search
  const isValidSearchValue = (value) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === "string") {
      return value.trim().length > 0;
    }
    return !!value;
  };

  // Update isSearchActive based on current search state
  useEffect(() => {
    let active = false;

    if (page === "callLogs") {
      if (searchBy === "date_range") {
        active =
          Array.isArray(searchValue) &&
          searchValue.length === 2 &&
          searchValue[0] &&
          searchValue[1];
      } else {
        active = searchBy && searchValue;
      }
    } else if (page === "superAdminDashboard") {
      if (showReports && searchBy === "date_range") {
        active = selectedRange && selectedRange.length === 2;
      } else if (searchBy === "createdAt") {
        active =
          searchValue && Array.isArray(searchValue) && searchValue.length === 2;
      } else if (searchBy === "province") {
        active = selectedCountry && checkboxValues && checkboxValues.length > 0;
      } else if (
        ["type", "tier", "denomination", "paymentTerm", "country", "orgActiveStatus"].includes(
          searchBy
        )
      ) {
        active = searchBy && checkboxValues && checkboxValues.length > 0;
      } else if (["name", "carePersonName"].includes(searchBy)) {
        active =
          searchValue &&
          typeof searchValue === "string" &&
          searchValue.trim().length > 0;
      } else {
        active = searchBy && isValidSearchValue(searchValue);
      }
    } else if (page === "subscriberDashboard") {
      active = selectedRange && selectedRange.length === 2;
    }

    setIsSearchActive(active);
  }, [
    searchBy,
    searchValue,
    selectedRange,
    selectedCountry,
    checkboxValues,
    page,
    showReports,
  ]);

  useEffect(() => {
    let filterString = "";

    if (page === "callLogs") {
      filterString = buildCallLogsFilterString(searchParams);
    } else {
      Object.keys(searchParams).map((key) => {
        if (key === "date_range") {
          const newValue = searchParams["date_range"].map((date) =>
            format(new Date(date), "yyyy-MM-dd")
          );
          filterString += `start_date=${newValue[0]}&end_date=${newValue[1]}`;
        } else {
          filterString += new URLSearchParams(searchParams).toString();
        }
      });
    }

    setFilterString && setFilterString(filterString);
  }, [searchParams, page, setFilterString]);

  const handleSearch = () => {
    // Only proceed if search is active
    if (!isSearchActive) return;

    // Handle different types of search values
    let searchParamValue;
    if (
      [
        "type",
        "tier",
        "denomination",
        "paymentTerm",
        "country",
        "province",
        "orgActiveStatus",
      ].includes(searchBy)
    ) {
      searchParamValue = checkboxValues;
    } else if (typeof searchValue === "string") {
      searchParamValue = searchValue.trim();
    } else {
      searchParamValue = searchValue;
    }

    setSearchParams({ ...searchParams, [searchBy]: searchParamValue });
  };

  const removeSearchParams = (key) => {
    setSearchParams(({ [key]: value, ...searchParams }) => searchParams);
  };

  const removeValue = (value, arr, index) => {
    // If the value at the current array index matches the specified value (2)
    if (arr.includes(value)) {
      // Removes the value from the original array
      arr.splice(index, 1);
    }
    return arr;
  };

  // Pass the removeValue function into the filter function to return the specified value
  const handleRange = (selectedRange) => {
    setSelectedRange(selectedRange);
    setDateRange(selectedRange);
  };

  const handleOrganizationSearch = () => {
    // Only proceed if search is active
    if (!isSearchActive) return;

    if (filterByArray.indexOf(searchBy) === -1) {
      // Special handling for date ranges
      if (
        searchBy === "createdAt" &&
        Array.isArray(searchValue) &&
        searchValue.length === 2
      ) {
        // Format dates for filtering
        const formattedValue = {
          start: searchValue[0],
          end: searchValue[1],
        };
        setFilterByArray([...filterByArray, searchBy]);
        setFilterValueArray([...filterValueArray, formattedValue]);
      } else if (
        [
          "type",
          "tier",
          "denomination",
          "paymentTerm",
          "country",
          "province",
           "orgActiveStatus",
        ].includes(searchBy)
      ) {
        // Handle checkbox selections
        setFilterByArray([...filterByArray, searchBy]);
        setFilterValueArray([...filterValueArray, checkboxValues]);
      } else {
        // Normal handling for other filter types (text inputs)
        const valueToStore =
          typeof searchValue === "string" ? searchValue.trim() : searchValue;
        setFilterByArray([...filterByArray, searchBy]);
        setFilterValueArray([...filterValueArray, valueToStore]);
      }
      setSearchBy("");
      setSearchValue("");
      setCheckboxValues([]);
      setSelectedCountry(null);
      setIsSearchActive(false);
    }
  };

  // Add a debounced search function for name searches
  const handleNameSearch = (value, type) => {
    setSearchValue(value);

    // Clear any existing timeout
    if (nameSearchTimeout) {
      clearTimeout(nameSearchTimeout);
    }

    if (!["name", "carePersonName"].includes(type)) {
      console.warn("Invalid type provided to handleNameSearch");
      return;
    }

    // Set a new timeout to delay the search
    const timeoutId = setTimeout(() => {
      const trimmedValue = value.trim();
      if (trimmedValue.length > 0) {
        // Check if this filter type already exists
        const existingIndex = filterByArray.indexOf(type);

        if (existingIndex === -1) {
          // Add new filter if it doesn't exist
          setFilterByArray([...filterByArray, type]);
          setFilterValueArray([...filterValueArray, trimmedValue]);
        } else {
          // Update existing filter value
          const newFilterValues = [...filterValueArray];
          newFilterValues[existingIndex] = trimmedValue;
          setFilterValueArray(newFilterValues);

          // We need to manually trigger a filter update
          // Since we're updating the array directly without changing its reference
          const newFilterBy = [...filterByArray];
          setFilterByArray([]); // Clear the array
          setTimeout(() => {
            // Then set it back with the updated values
            setFilterByArray(newFilterBy);
          }, 0);
        }
      } else {
        // If search term is empty, remove the filter
        const existingIndex = filterByArray.indexOf(type);
        if (existingIndex !== -1) {
          const newFilterBy = [...filterByArray];
          const newFilterValue = [...filterValueArray];
          newFilterBy.splice(existingIndex, 1);
          newFilterValue.splice(existingIndex, 1);

          setFilterByArray(newFilterBy);
          setFilterValueArray(newFilterValue);
        }
      }
    }, 500); // 500ms delay

    setNameSearchTimeout(timeoutId);
  };

  // Handle checkbox value changes
  const handleCheckboxChange = (values) => {
    setCheckboxValues(values);
  };

  // Reset states when searchBy changes
  useEffect(() => {
    setSearchValue("");
    setCheckboxValues([]);
    if (searchBy !== "province") {
      setSelectedCountry(null);
    }
  }, [searchBy]);

  return page && page === "callLogs" ? (
    <Container>
      <Wrapper className="p-4 mb-1">
        <SearchGroup>
          <StyledSelect
            name="searchBy"
            onChange={(e) => {
              setSearchValue("");
              setSearchBy(e.target.value);
            }}
          >
            <option value="">Search By</option>
            <option value="date_range">Date Range</option>
            <option value="agent_name">Agent Name</option>
            <option value="caller_number">Caller Number</option>
            <option value="called_number">Organization Number</option>
            <option value="date">Date</option>
          </StyledSelect>

          <FilterWrapper>
            {searchBy === "date_range" ? (
              <DateRange
                placeholder="Select Date Range"
                value={Array.isArray(searchValue) ? searchValue : []}
                onChange={(range) => {
                  if (range?.length === 2) {
                    setSearchValue(range);
                    setSearchParams((prev) => ({ ...prev, date_range: range }));
                  } else if (!range) {
                    setSearchValue("");
                    setSearchParams((prev) => {
                      const { date_range, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
              />
            ) : (
              <StyledInput
                type="search"
                placeholder="Search"
                aria-label="Search"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                }}
                id="search-input"
                aria-describedby="search-button"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && isSearchActive) {
                    handleSearch();
                  }
                }}
              />
            )}
            <SearchButton
              type="button"
              id="search-button"
              onClick={handleSearch}
              disabled={!isSearchActive}
              isActive={isSearchActive}
              title={
                isSearchActive
                  ? "Click to search"
                  : "Select search criteria first"
              }
            >
              <MdSearch />
            </SearchButton>
          </FilterWrapper>
        </SearchGroup>
      </Wrapper>
      <SearchWrapper className="ml-5 mb-2">
        {Object.keys(searchParams).length > 0 ? (
          Object.values(searchParams).map((param, index) => {
            return (
              <p className="p-1 m-1 rounded" key={index}>
                {Object.keys(searchParams)[index]}:
                {
                  //checking if the param is an array so if its the date range
                  Array.isArray(param) ? (
                    <span>
                      {" "}
                      `{param[0].toLocaleDateString("en-US")} -{" "}
                      {param[1].toLocaleDateString("en-US")}`
                    </span>
                  ) : (
                    <span>`{param}` </span>
                  )
                }
                <MdClear
                  onClick={() => {
                    const key = Object.keys(searchParams)[index];
                    if (key === "date_range") setSearchValue("");
                    removeSearchParams(key);
                  }}
                />
              </p>
            );
          })
        ) : (
          // For callLogs page show current month badge when no filter is applied
          page === "callLogs" && (
            <p className="p-1 m-1 rounded">
              {(() => {
                const now = new Date();
                const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                const endDate = now; // show up to today
                return (
                  <>
                    date_range: `{startOfMonth.toLocaleDateString("en-US")} - {endDate.toLocaleDateString("en-US")}`
                  </>
                );
              })()}
              <MdClear
                onClick={() => {
                  // clearing current month badge will clear any date_range in state
                  setSearchParams((prev) => {
                    const { date_range, ...rest } = prev;
                    return rest;
                  });
                }}
              />
            </p>
          )
        )}
      </SearchWrapper>
    </Container>
  ) : page && page === "superAdminDashboard" ? (
    <Container>
      <Wrapper className="p-4 mb-1">
        <SearchGroup>
          <StyledSelect
            name="searchBy"
            value={searchBy}
            onChange={(e) => {
              setSearchBy(e.target.value);
              setSearchValue("");
              // Reset selected country when changing search type
              if (e.target.value !== "province") {
                setSelectedCountry(null);
              }
            }}
          >
            <option value="">{showReports ? "Filter By" : "Search By"}</option>
            {!showReports && (
              <>
                <option value="type">Organization Type</option>
                <option value="orgActiveStatus">Organization Status</option>
                <option value="assignedNumber">Organization Toll Number</option>
                <option value="tier">Organization Tier</option>
                <option value="name">Organization Name</option>
                <option value="carePersonName">Care Person Name</option>
                <option value="denomination">Denomination</option>
                <option value="country">Country</option>
                <option value="province">Province/State</option>
                <option value="paymentTerm">Payment Term</option>
                <option value="createdAt">Date Created</option>
              </>
            )}

            {showReports && <option value="date_range">Date Range</option>}
          </StyledSelect>

          <FilterWrapper>
            {searchBy === "date_range" ? (
              <DateRange
                id="selectedRange"
                value={selectedRange}
                ranges={predefinedBottomRanges}
                placeholder="Select Date Range"
                onChange={(e) => {
                  e && handleRange(e);
                }}
              />
            ) : searchBy === "createdAt" ? (
              <DateRange
                format="MM/dd/yyyy"
                id="createdAtRange"
                placeholder="Select Date Range"
                onChange={(e) => {
                  e && setSearchValue(e);
                }}
              />
            ) : searchBy === "type" ? (
              <CheckboxSelect
                label={"Types"}
                optionArray={organizationTypes}
                selectedValue={checkboxValues}
                setSelectedValue={handleCheckboxChange}
              />
            ) : searchBy === "tier" ? (
              <CheckboxSelect
                label={"Tiers"}
                optionArray={organizationTiers}
                selectedValue={checkboxValues}
                setSelectedValue={handleCheckboxChange}
              />
            ) : searchBy === "denomination" ? (
              <CheckboxSelect
                label={"Denominations"}
                optionArray={organizationDenominations}
                selectedValue={checkboxValues}
                setSelectedValue={handleCheckboxChange}
              />
            ) : searchBy === "paymentTerm" ? (
              <CheckboxSelect
                label={"Payment Terms"}
                optionArray={paymentTerms}
                selectedValue={checkboxValues}
                setSelectedValue={handleCheckboxChange}
              />
            )
              : searchBy === "orgActiveStatus" ? (
              <CheckboxSelect
                label={"Active Status"}
                optionArray={OrgActiveStatusList}
                selectedValue={checkboxValues}
                setSelectedValue={handleCheckboxChange}
              />
            ) : searchBy === "country" ? (
              <CheckboxSelect
                label={"Countries"}
                optionArray={countries}
                selectedValue={checkboxValues}
                setSelectedValue={handleCheckboxChange}
              />
            ) : searchBy === "province" ? (
              <div className="flex flex-row w-full gap-2">
                <div className={`${selectedCountry ? "w-1/3" : "w-full"}`}>
                  <select
                    className="w-full p-2 border border-gray-300 rounded"
                    onChange={(e) => {
                      // Find the country object by value
                      const country = countries.find(
                        (c) => c.value === e.target.value
                      );
                      setSelectedCountry(country);
                    }}
                  >
                    <option value="">Select a country</option>
                    {countries.map((country) => (
                      <option key={country.id} value={country.value}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedCountry && (
                  <div className="w-2/3">
                    <CheckboxSelect
                      label={`${selectedCountry.label} Provinces/States`}
                      optionArray={provinces[selectedCountry.value] || []}
                      selectedValue={checkboxValues}
                      setSelectedValue={handleCheckboxChange}
                    />
                  </div>
                )}
              </div>
            ) : searchBy === "name" ? (
              <StyledInput
                type="text"
                placeholder="Search by organization name..."
                aria-label="Search"
                value={searchValue}
                onChange={(e) => handleNameSearch(e.target.value, "name")}
                id="search-input"
                aria-describedby="search-button"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && isSearchActive) {
                    handleOrganizationSearch();
                  }
                }}
              />
            ) : searchBy === "carePersonName" ? (
              <StyledInput
                type="text"
                placeholder="Search by care person name..."
                aria-label="Search"
                value={searchValue}
                onChange={(e) =>
                  handleNameSearch(e.target.value, "carePersonName")
                }
                id="search-care-person-name"
                aria-describedby="search-button"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && isSearchActive) {
                    handleOrganizationSearch();
                  }
                }}
              />
            ) : (
              <StyledInput
                type="text"
                placeholder={
                  searchBy
                    ? `Search by ${searchBy
                        .replace(/([A-Z])/g, " $1")
                        .toLowerCase()}...`
                    : "Select search type first"
                }
                aria-label="Search"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                }}
                disabled={!searchBy || searchBy === "date_range"}
                id="search-input"
                aria-describedby="search-button"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && isSearchActive) {
                    handleOrganizationSearch();
                  }
                }}
              />
            )}
            <SearchButton
              type="button"
              id="search-button"
              onClick={handleOrganizationSearch}
              disabled={!isSearchActive}
              isActive={isSearchActive}
              title={
                isSearchActive
                  ? "Click to apply filter"
                  : "Select filter criteria first"
              }
            >
              <MdSearch />
            </SearchButton>
          </FilterWrapper>
        </SearchGroup>
      </Wrapper>
      <SearchWrapper className="ml-5 mb-2">
        {filterByArray.length > 0 &&
          filterByArray.map((filterBy, index) => {
            return (
              <p className="p-1 m-1 rounded" key={index}>
                {filterBy[0]?.toUpperCase() + filterBy.slice(1)}:
                {filterBy === "createdAt" &&
                typeof filterValueArray[index] === "object" ? (
                  <span>
                    `
                    {filterValueArray[index].start instanceof Date
                      ? filterValueArray[index].start.toLocaleDateString(
                          "en-US"
                        )
                      : new Date(
                          filterValueArray[index].start
                        ).toLocaleDateString("en-US")}{" "}
                    -
                    {filterValueArray[index].end instanceof Date
                      ? filterValueArray[index].end.toLocaleDateString("en-US")
                      : new Date(
                          filterValueArray[index].end
                        ).toLocaleDateString("en-US")}
                    `
                  </span>
                ) : Array.isArray(filterValueArray[index]) ? (
                  filterValueArray[index].map((value, id) => {
                    return <span key={id}>`{String(value)}` </span>;
                  })
                ) : (
                  <span>`{String(filterValueArray[index])}` </span>
                )}
                <MdClear
                  onClick={() => {
                    setFilterByArray([
                      ...removeValue(filterBy, filterByArray, index),
                    ]);
                    setFilterValueArray([
                      ...removeValue(
                        filterValueArray[index],
                        filterValueArray,
                        index
                      ),
                    ]);
                    setSearchBy("");
                  }}
                />
              </p>
            );
          })}
        {dateRange && dateRange.length === 2 && (
          <p className="p-1 m-1 rounded">
            Date Range: `
            {dateRange[0] instanceof Date
              ? dateRange[0].toLocaleDateString("en-US")
              : new Date(dateRange[0]).toLocaleDateString("en-US")}{" "}
            -
            {dateRange[1] instanceof Date
              ? dateRange[1].toLocaleDateString("en-US")
              : new Date(dateRange[1]).toLocaleDateString("en-US")}
            `
            <MdClear
              onClick={() => {
                setDateRange("");
                setSelectedRange([]);
                setSearchBy("");
              }}
            />
          </p>
        )}
      </SearchWrapper>
    </Container>
  ) : page && page === "subscriberDashboard" ? (
    <Container>
      <Wrapper className="p-4 mb-1">
        <SearchGroup>
          <FilterWrapper>
            <DateRange
              id="selectedRange"
              value={selectedRange}
              ranges={predefinedBottomRanges}
              placeholder="Select Date Range"
              onChange={(e) => {
                e && handleRange(e);
              }}
            />
            <SearchButton
              type="button"
              id="search-button"
              onClick={() => {}}
              disabled={!isSearchActive}
              isActive={isSearchActive}
              title={
                isSearchActive ? "Date range selected" : "Select date range"
              }
            >
              <MdSearch />
            </SearchButton>
          </FilterWrapper>
        </SearchGroup>
      </Wrapper>
      <SearchWrapper className="ml-5 mb-2">
        {dateRange && (
          <p className="p-1 m-1 rounded">
            Date Range: `{dateRange[0].toLocaleDateString("en-US")} -{" "}
            {dateRange[1].toLocaleDateString("en-US")}`
            <MdClear
              onClick={() => {
                setDateRange("");
                setSelectedRange([]);
              }}
            />
          </p>
        )}
      </SearchWrapper>
    </Container>
  ) : (
    <></>
  );
};

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: start;
  font-family: "Futura PT";
  font-size: 16px;
  height: auto;
  min-height: 60px;

  div {
    height: 100%;
  }
`;

export { SearchWrapper };

const SearchGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 600px;
`;

const StyledSelect = styled.select`
  padding: 3px 6px;
  border: 2px solid #0d5e6d;
  border-radius: 30px;
  background-color: #0d5e6d;
  color: white;
  font-family: "Futura PT";
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.15em;
  outline: none;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 25px;
  min-width: 140px;

  &:hover {
    background-color: #0a4a57;
  }

  option {
    background-color: white;
    color: #333;
    padding: 8px;
  }
`;

const FilterWrapper = styled.div`
  display: flex;
  align-items: center;
  border: 1.8px solid #106271;
  border-radius: 30px;
  background-color: white;
  overflow: hidden;
  flex: 1;
  max-width: 350px;
  max-height: 36px;
  padding-left: 12px;
  padding-right: 4px;
`;

const StyledInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-family: "Futura PT";
  font-weight: 400;
  font-size: 14px;
  background-color: transparent;
  letter-spacing: 0.15em;
  color: #333;
  min-width: 0;

  &::placeholder {
    color: #888;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const SearchButton = styled.button`
  padding: 0;
  background-color: ${(props) => (props.isActive ? "#0d5e6d" : "transparent")};
  border: none;
  color: ${(props) => (props.isActive ? "white" : "#888")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  transition: all 0.2s ease-in-out;
  box-shadow: ${(props) =>
    props.isActive ? "0 2px 4px rgba(0,0,0,0.2)" : "none"};

  &:hover {
    background-color: ${(props) =>
      props.isActive
        ? "#0a4a57"
        : props.disabled
        ? "transparent"
        : "transparent"};
    transform: ${(props) => (props.isActive ? "scale(1.05)" : "none")};
  }

  &:active {
    transform: ${(props) => (props.isActive ? "scale(0.95)" : "none")};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  justify-content: start;
  align-items: start;
  flex-direction: column;
`;

const SearchWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;

  p {
    background-color: #86ccda;
    color: white;
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 14px;
    gap: 6px;
  }

  svg {
    cursor: pointer;
    opacity: 0.8;

    &:hover {
      opacity: 1;
    }
  }
`;

const DateRange = styled(DateRangePicker)`
  flex: 1;
  min-width: 0;

  .rs-picker-toggle {
    border: none;
    padding: 8px 12px;
    background: transparent;

    &:hover {
      background: transparent;
    }
  }

  .rs-picker-toggle-wrapper {
    flex: 1;
  }
`;
